import { router } from '@inertiajs/react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/Card";
import { Button } from "@/components/Button";
import { Badge } from "@/components/Badge";
import { Input } from "@/components/Input";
import { Building2, Plus, Edit, Power, Trash2, Users, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { useConfirm } from "@/hooks/useConfirm";
import { useState } from "react";
import AppLayout from '@/layouts/AppLayout';

export default function DepartmentsIndex({ departments = [] }) {
  const { confirm, ConfirmDialog } = useConfirm();
  const [isCreating, setIsCreating] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({ name: '', description: '' });

  const handleSubmit = (e) => {
    e.preventDefault();

    if (editingId) {
      router.patch(`/admin/departments/${editingId}`, {
        department: formData
      }, {
        onSuccess: () => {
          setEditingId(null);
          setFormData({ name: '', description: '' });
        }
      });
    } else {
      router.post('/admin/departments', {
        department: formData
      }, {
        onSuccess: () => {
          setIsCreating(false);
          setFormData({ name: '', description: '' });
        }
      });
    }
  };

  const handleEdit = (dept) => {
    setEditingId(dept.id);
    setFormData({ name: dept.name, description: dept.description || '' });
    setIsCreating(false);
  };

  const handleCancel = () => {
    setIsCreating(false);
    setEditingId(null);
    setFormData({ name: '', description: '' });
  };

  const handleToggleActive = async (deptId, isActive) => {
    const confirmed = await confirm({
      title: isActive ? 'Desactivar departamento' : 'Activar departamento',
      message: `¿Estás seguro de ${isActive ? 'desactivar' : 'activar'} este departamento?`,
      confirmText: isActive ? 'Desactivar' : 'Activar',
      variant: isActive ? 'warning' : 'success'
    });

    if (!confirmed) return;

    router.post(`/admin/departments/${deptId}/toggle_active`, {}, {
      preserveScroll: true
    });
  };

  const handleDelete = async (dept) => {
    const confirmed = await confirm({
      title: 'Eliminar departamento',
      message: `¿Estás seguro de eliminar "${dept.name}"? ${dept.users_count > 0 ? `Este departamento tiene ${dept.users_count} usuario(s) asignado(s) y no podrá ser eliminado.` : 'Esta acción no se puede deshacer.'}`,
      confirmText: 'Eliminar',
      variant: 'danger'
    });

    if (!confirmed) return;

    router.delete(`/admin/departments/${dept.id}`, {
      preserveScroll: true
    });
  };

  return (
    <div className="py-8 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
                <Building2 className="w-8 h-8 text-blue-500" />
                Departamentos
              </h1>
              <p className="text-gray-600 mt-1">
                Gestiona los departamentos de la organización
              </p>
            </div>
            {!isCreating && !editingId && (
              <Button
                onClick={() => setIsCreating(true)}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                <Plus className="w-4 h-4 mr-2" />
                Nuevo departamento
              </Button>
            )}
          </div>
        </div>

        {/* Form Create/Edit */}
        {(isCreating || editingId) && (
          <Card className="mb-6 shadow-lg border-2 border-blue-200">
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>{editingId ? 'Editar Departamento' : 'Nuevo Departamento'}</span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleCancel}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X className="w-4 h-4" />
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nombre <span className="text-red-500">*</span>
                  </label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Ej: Tecnología, Ventas, Marketing..."
                    required
                    className="w-full"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Descripción (opcional)
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Descripción del departamento..."
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div className="flex gap-2 justify-end">
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={handleCancel}
                  >
                    Cancelar
                  </Button>
                  <Button
                    type="submit"
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    {editingId ? 'Actualizar' : 'Crear'}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Departments List */}
        <div className="space-y-4">
          {departments.map((dept) => (
            <Card
              key={dept.id}
              className={cn(
                "shadow hover:shadow-lg transition-shadow",
                editingId === dept.id && "border-2 border-blue-300"
              )}
            >
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                        <Building2 className="w-6 h-6 text-blue-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-lg text-gray-900">{dept.name}</h3>
                        {dept.description && (
                          <p className="text-sm text-gray-600 mt-0.5">{dept.description}</p>
                        )}
                        <div className="flex items-center gap-2 mt-1">
                          <Badge className="bg-gray-100 text-gray-600 text-xs">
                            <Users className="w-3 h-3 mr-1" />
                            {dept.users_count} usuario{dept.users_count !== 1 ? 's' : ''}
                          </Badge>
                          <Badge className={cn(
                            "text-xs",
                            dept.active
                              ? "bg-green-100 text-green-700"
                              : "bg-gray-100 text-gray-700"
                          )}>
                            {dept.active ? "✅ Activo" : "⏸️ Inactivo"}
                          </Badge>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleEdit(dept)}
                      className="text-blue-600 hover:bg-blue-50"
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleToggleActive(dept.id, dept.active)}
                      className={cn(
                        dept.active
                          ? "text-yellow-600 hover:bg-yellow-50"
                          : "text-green-600 hover:bg-green-50"
                      )}
                    >
                      <Power className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleDelete(dept)}
                      className="text-red-600 hover:bg-red-50"
                      disabled={dept.users_count > 0}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Empty State */}
        {departments.length === 0 && (
          <Card className="text-center py-12">
            <CardContent>
              <Building2 className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-700 mb-2">
                No hay departamentos configurados
              </h3>
              <p className="text-gray-500 mb-4">
                Crea tu primer departamento para comenzar
              </p>
              <Button
                onClick={() => setIsCreating(true)}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                <Plus className="w-4 h-4 mr-2" />
                Crear Departamento
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Back Button */}
        <div className="mt-6">
          <Button
            variant="ghost"
            onClick={() => router.visit('/admin')}
            className="text-gray-600 hover:text-gray-900"
          >
            ← Volver al Panel de Admin
          </Button>
        </div>
      </div>

      <ConfirmDialog />
    </div>
  );
}

DepartmentsIndex.layout = page => <AppLayout children={page} />;
